import {Component} from "react";
import AppConfig from "../../config/config.json";
import {
    defineSpecificUrl,
    formatConfig,
    getConfigAsync,
    getTimelineAsync,
    isAuthorized,
    isDomainAuthorized,
    isTemplateConfig
} from "../../helpers/configHelper";
import {activeSpectrum, createAudioMotionSpectrum} from "../../helpers/spectrumHelper";
import {updateMediaSession} from "../../helpers/mediaSessionHelper";
import Hls from "hls.js";

export default class Player extends Component {
    volumeTimeout = null;
    titleToDisplay = null;
    authorToDisplay = null;
    imageToDisplay = null;
    audioMotion = null;
    queue = [];
    getConfigIntervalFunction = null;
    reduceHlsTimeIntervalFunction = null;
    isChangingImage = false;
    hlsPlayer = null;

    /* ------------------------------------------------------------

                    Init Functions

    ------------------------------------------------------------ */

    constructor(props) {
        super(props);
        var configUrl = AppConfig.urlConfig.replace('{player_uuid}', props.configId);
        if (props.templateName) {
            let base = '';
            if (AppConfig.basename !== '') {
                base = '/' + AppConfig.basename;
            }
            configUrl = base + '/config-' + props.templateName + '.json';
        }

        if (AppConfig.env !== 'prod') {
            window.IPS.config = {
                url: 'ws://preprod-info-mq.infomaniak.com',
                user: 'ips:ips-public',
                pass: '4fBt5AdC2P'
            };
        }


        this.state = {
            autoplay: !!parseInt(props.autoplay),
            configId: props.configId,
            configUrl: configUrl,
            controls: false,
            domainAuthorized: false,
            error: false,
            isExtend: false,
            light: false,
            loaded: 0,
            locale: props.locale,
            muted: false,
            neverPlayed: true,
            playbackRate: 1.0,
            played: 0,
            playing: false,
            preview: props.preview,
            bufferPreviewed: props.bufferPreviewed,
            onChangeCommittedFlush: props.onChangeCommittedFlush,
            previewConfig: {},
            stationIndex: 0,
            stream: null,
            timeline: [],
            tokens: props.tokens,
            urlParam: props.url,
            previousPlayTime: 0,
        }
    }

    async componentDidMount() {

        let config = await getConfigAsync(this.state.configUrl);
        if (!config) {
            //If no config found return to 404 page
            window.location.href = '/404';
        }

        if (isTemplateConfig(this.state.configUrl) || isDomainAuthorized(this.state.configId)) {
            this.setState({domainAuthorized: true});
        } else {
            //If not authorized return to 401 page
            window.location.href = '/401';
        }

        window.addEventListener("offline", (e) => {
            this.setState({wasOffline: true});
        });

        window.addEventListener("online", (e) => {
            if (this.state.wasOffline) {
                if (this.hlsPlayer) {
                    // Used to flush the buffer
                    this.hlsPlayer.trigger(Hls.Events.BUFFER_FLUSHING, {
                        startOffset: 0,
                        endOffset: Infinity,
                        type: 'audio'
                    });
                    this.hlsPlayer.startLoad();
                }
                this.subscribeRPSForLastMetadata();
                this.subscribeRPSForListener();
            }
        });

        // Override config
        // Create the player with a fake url for not load the real one and do a hit in statistics
        config.url = 'temp.mp3';
        if (this.state.urlParam) {
            config = defineSpecificUrl(this.state.urlParam, config);
        }

        this.init(config);
        if (!this.state.error && !this.state.urlParam) {
            // MODE PREVIEW
            if (this.state.preview) {
                this.addMessageListener(config);
                this.setState({previewConfig: config});
            }
        }

    }

    init = config => {
        // Need to keep new config get by addMessageListener in preview
        if (this.state.preview) {
            this.setState({previewConfig: config});
        }
        let configFormated = formatConfig(config, this.state);
        // Define a volume temp for come back to the initial value
        configFormated.volumeTemp = configFormated.volume;

        // Default display playername without author
        configFormated.title = configFormated.playername;
        configFormated.author = "";
        this.setState(configFormated, function () {
            getTimelineAsync(this.state.stations[this.state.stationIndex].id).then((timeline) => {
                this.setState({timeline}, function () {
                    this.subscribeRPSForLastMetadata();
                    this.subscribeRPSForListener();
                });
            })
        });
    }

    // Call at the creation or after load function
    // The config is not change here
    onPlayerReady = () => {
        // To announce availability to parent window
        window.top.postMessage('player-ready', '*')
        if (!this.state.error && !this.state.neverPlayed && this.state.stream.format === 'hls') {
            // Get the hls player, it's undefined if the stream is not a HLS stream
            this.hlsPlayer = this.player.getInternalPlayer('hls');
            if (this.hlsPlayer && this.state.isHlsBuffered && !this.state.isDirectControl) {

                //level set here
                this.hlsPlayer.loadLevel = 1;
                //play from the first available fragment available in the buffer
                // If bellow *3 sometimes the fragment doesnt exist anymore so it push the listener to the 'live' fragment, this is a trick to avoid this
                this.hlsPlayer.startLoad(this.state.fragment * 3);
            }

        }
        if (this.state.neverPlayed && this.state.autoplay) {
            this.handlePlay();
        }
    }

    /* ------------------------------------------------------------

                        Major Functions

    ------------------------------------------------------------ */

    // Call when user changes the quality
    load = stream => {
        if (stream.url !== this.state.url) {
            this.isLoading = true;
            // Apply a loading image for the transition between streams
            this.changeImageAndTitleAuthor(this.state.loadingUrl, this.state.playername, null);

            if (this.state.playing) {
                // Stop playing to remove stream loading
                this.handlePause();
            }

            // Error if the user is unauthorized
            if (!isTemplateConfig(this.state.configUrl) && !isAuthorized(stream)) {
                // change stream for display (like option selected)
                this.setState({stream: stream});
                this.setPlayerInError(this.state.unauthorizedUrl, this.state);
            } else {
                // Set to the player the new stream url and define stream is played
                // Reset the error status when the player listen another stream
                this.setState({url: stream.url, stream: stream, error: false}, function () {
                    // Create spectrum in loading, not in ready state because is useless to create a spectrum on the same stream
                    if (this.state.spectrum) {
                        createAudioMotionSpectrum(this);
                    }

                    // Force to play when user loads a new stream
                    if (!this.state.error) {
                        this.handlePlay();
                    }
                });
            }
            this.isLoading = false;
        }
    }

    handleSeekTo = (value, isCommitted) => {
        if (this.hlsPlayer) {
            const bufferSize = (this.state.playlist * this.state.fragment);
            const bufferedValue = this.player.getDuration() - bufferSize;
            this.setState({previousPlayTime: value - (bufferSize - this.state.fragment)});
            value += bufferedValue;

            if (this.hlsPlayer.url) {
                if (value <= this.hlsPlayer.bufferController.details.fragments[0].start + this.state.fragment) value += this.state.fragment * 2;
                if (isCommitted) this.player.seekTo(((value / bufferSize) * (bufferSize - this.state.fragment)));
            }
        }
    }


    // Call every second when the player is playing and if the stream is up
    handleProgress = state => {
        var timeline = this.state.timeline;
        let timeLineLength = 0;
        if (timeline && timeline.length !== undefined) {
            timeLineLength = timeline.length - 1;
        }
        let currentTimelineElement = timeline[timeLineLength];
        const currentPlay = ((Date.now() + (this.state.previousPlayTime * 1000)) / 1000);
        if (this.state.stream.format === 'hls') {
            let index = 0;
            if (timeline) {
                while (index < timeline.length && currentPlay > (timeline[index].date + this.state.fragment * 4)) {
                    currentTimelineElement = timeline[index];
                    index++;
                }
            }
            // when isHlsBuffered active and near of the end of it flushing and reloading the source
            if (this.state.isDirectControl && this.state.isHlsBuffered && this.player.getCurrentTime() + this.state.fragment * 3 > this.player.getSecondsLoaded()) {
                this.hlsPlayer.trigger(Hls.Events.BUFFER_FLUSHING, {
                    startOffset: 0,
                    endOffset: this.player.getSecondsLoaded(),
                    type: 'audio'
                });
                this.hlsPlayer.startLoad(this.state.fragment * 3);
            }
        }

        this.titleToDisplay = currentTimelineElement.title;
        this.authorToDisplay = currentTimelineElement.author;

        if (currentTimelineElement.cover && this.state.cover) {
            this.imageToDisplay = currentTimelineElement.cover;
        } else {
            if (this.state.is_station_thumbnail && this.state.stations.length > 1) {
                this.imageToDisplay = this.state.stations[this.state.stationIndex].thumbnail;
            } else if (this.state.thumbnail) {
                this.imageToDisplay = this.state.thumbnail;
            } else {
                this.imageToDisplay = AppConfig.storage + 'defaultImage.svg';
            }
        }

        this.changeImageAndTitleAuthor(this.imageToDisplay, this.titleToDisplay, this.authorToDisplay);
        this.setState(state);
    }

    handlePlay = () => {
        // First play
        if (this.state.neverPlayed) {
            // Apply a background-color transition after the first play to not trigger a transition at the loading page
            document.body.style.transition = 'background-color 1s';
            this.setState({neverPlayed: false});
        }
        if (!isTemplateConfig(this.state.configUrl) && !isAuthorized(this.state.stream)) {
            this.setPlayerInError(this.state.unauthorizedUrl);
        } else if (!this.state.error) {
            // Start loading
            if (this.state.stream.format === 'hls') {
                // reload HLS stream at the end
                this.setState({url: this.state.stream.url}, function () {
                    // Create spectrum
                    if (this.state.spectrum) {
                        createAudioMotionSpectrum(this);
                    }

                    const bufferSize = this.state.fragment * this.state.playlist;
                    const playerTime = this.state.previousPlayTime + bufferSize;
                    if (playerTime <= this.state.fragment) {
                        this.setState({previousPlayTime: -(bufferSize - this.state.fragment)})
                        this.player.seekTo(this.state.fragment * 3)
                    } else {
                        if (!this.state.isDirectControl && this.state.isHlsBuffered) {
                            this.setState({previousPlayTime: -(bufferSize + this.state.fragment)});
                            this.player.seekTo(-(this.state.fragment * 3));

                            this.player.seekTo(bufferSize - this.state.fragment * 3, 'seconds');
                        } else {
                            // Be careful to not seek to the max length of the buffer
                            this.player.seekTo(-(this.state.fragment * 3));
                        }
                    }
                });
                clearInterval(this.reduceHlsTimeIntervalFunction);
                this.reduceHlsTimeIntervalFunction = null;
            } else {
                this.player.player.player.player.src = this.state.stream.url;
                this.player.player.player.player.load();
                if (this.state.spectrum) {
                    createAudioMotionSpectrum(this);
                }
            }

            // With url in the param this function doesn't exist
            if (this.getConfigIntervalFunction) {
                // Force to get the last config
                this.getConfigIntervalFunction(true);
            }
        }
        this.setState({playing: true});
        window.top.postMessage('playing', '*');
    }

    handlePause = () => {
        // Stop loading
        if (this.state.stream.format === 'hls' && this.hlsPlayer) {
            this.hlsPlayer.stopLoad();
            // change the stream url for reload the right stream at the play
            this.setState({url: ''});
            this.reduceHlsTimeIntervalFunction = setInterval(() => {
                this.setState({previousPlayTime: this.state.previousPlayTime - 1})
            }, 1000);

        } else {
            // Empty the src player to stop loading
            this.player.player.player.player.src = '';
            this.player.player.player.player.load();
        }
        this.setState({playing: false});
        window.top.postMessage('paused', '*');
    }


    changeImageAndTitleAuthor = (url, title, author = '') => {
        if (!this.state.is_metadata_display) {
            title = this.state.playername;
            author = '';
        }

        const imageHTML = document.getElementById('image');
        let spectrumHTML = document.getElementById('spectrum');
        const titleHTML = document.getElementById('title-container');
        const authorHTML = document.getElementById('author-container');
        if (this.isChangingImage === false) {
            if (url !== this.state.display || title !== this.state.title || author !== this.state.author) {
                this.isChangingImage = true;
                if (spectrumHTML && (url !== this.state.display || !this.state.spectrum) && !activeSpectrum(this, url)) {
                    spectrumHTML.classList.add("fadeOut");
                }

                if (url !== this.state.display || activeSpectrum(this)) {
                    imageHTML?.classList?.add("fadeOut");
                }

                if (!title) {
                    if (author) {
                        title = author;
                        author = '';
                    } else {
                        title = this.state.playername;
                    }
                }

                if (this.state.title !== title) {
                    titleHTML.classList.add("fadeOut");
                }

                if (this.state.author !== author) {
                    authorHTML.classList.add("fadeOut");
                }

                let player = this;
                // Wait the fadeOut end
                setTimeout(function () {
                    let previousUrl = player.state.display;
                    player.setState({display: url, title: title, author: author}, function () {
                        player.handleMarquee('title-container');
                        player.handleMarquee('author-container');
                        if (previousUrl === url) {
                            spectrumHTML = document.getElementById('spectrum');
                            if (activeSpectrum(player) && spectrumHTML) {
                                spectrumHTML.classList.remove("fadeOut");
                            }
                        }
                        if (url && url.split('.').pop() === 'svg') {
                            player.onLoadImage();
                        }
                        titleHTML.classList.remove("fadeOut");
                        authorHTML.classList.remove("fadeOut");
                        // Wait the fadeIn end
                        setTimeout(function () {
                            player.isChangingImage = false;
                            updateMediaSession(player);
                            if (player.queue.length > 0) {
                                let next = player.queue.shift();
                                player.changeImageAndTitleAuthor(next.url, next.title, next.author);
                            }
                        }, 1000);
                    });
                }, 1100);
            } else if (this.queue.length > 0) {
                let next = this.queue.shift();
                this.changeImageAndTitleAuthor(next.url, next.title, next.author);
            }
        } else if (!(this.queue.find((element) => element.url === url && element.title === title && element.author === author))) {
            this.queue.push({
                url: url,
                title: title,
                author: author
            })
        }
    }

    setPlayerInError = (imageToDisplay, config = this.state) => {
        if (!config.error || this.isLoading) {
            this.setState({error: true, url: "temp.mp3"});
            this.queue = [];
            this.changeImageAndTitleAuthor(imageToDisplay, config.translations.error, null);
        }
    }

    subscribeRPSForLastMetadata = () => {
        if (window.IPS) {
            window.IPS.subscribe(
                'radio', {'uid': 'station/' + this.state.stations[this.state.stationIndex].id + '/last_metadata'},
                (res, mixed) => {
                    if (res === true) {
                        mixed.on('radio', data => {
                            const string = String.fromCharCode.apply(String, data);
                            try {
                                let lastMetadata = JSON.parse(JSON.parse(string));
                                this.setState({'timeline': [...this.state.timeline, lastMetadata]});
                            } catch (error) {
                                console.log(error)
                            }
                        });
                    } else {
                        console.log("WS subscribe fail for last metadata")
                    }
                }
            );
        }
    }

    unsubscribeRPSForLastMetadata = () => {
        if (window.IPS) {
            window.IPS.unsubscribe('radio', {'uid': 'station/' + this.state.stations[this.state.stationIndex].id + '/last_metadata'});
        }
    }

    subscribeRPSForListener = () => {
        if (window.IPS && this.state.displayListener) {
            window.IPS.subscribe(
                'radio', {'uid': 'station/' + this.state.stations[this.state.stationIndex].id + '/listener'},
                (res, mixed) => {
                    if (res === true) {
                        mixed.on('radio', data => {
                            const string = String.fromCharCode.apply(String, data);
                            try {
                                this.setState({numberListener: JSON.parse(string)});
                            } catch (error) {
                                console.log(error)
                            }
                        });
                    } else {
                        console.log("WS subscribe fail for listener")
                    }
                }
            );
        }
    }

    unsubscribeRPSForListener = () => {
        if (window.IPS && this.state.displayListener) {
            window.IPS.unsubscribe('radio', {'uid': 'station/' + this.state.stations[this.state.stationIndex].id + '/listener'});
        }
    }


    addMessageListener = () => {
        const player = this;
        window.addEventListener('message', event => {
            let newConfig;
            try {
                newConfig = JSON.parse(event.data);
            } catch (e) {
                return;
            }
            player.init(newConfig);
        });
    }

    /* ------------------------------------------------------------

                            Minor Functions

    ------------------------------------------------------------ */
    handleOpenOverlay = overlayName => {
        const overlayHTML = document.getElementById(overlayName + "-overlay");
        if (overlayHTML) {
            overlayHTML.classList.add("show-overlay");
        }
    }

    handleCloseOverlay = overlayName => {
        const overlayHTML = document.getElementById(overlayName + "-overlay");
        if (overlayHTML) {
            overlayHTML.classList.remove("show-overlay");
        }
    }

    handleVolumeChange = (e, value) => {
        this.setState({muted: value === 0, volume: parseFloat(value)});
    }

    handleToggleMuted = () => {
        const isMuted = this.state.muted;
        if (!isMuted) {
            this.setState({volumeTemp: this.state.volume});
            this.handleVolumeChange(null, 0);
        } else {
            this.handleVolumeChange(null, this.state.volumeTemp);
        }
        this.setState({muted: !isMuted});
    }

    handleVolumeHoverEnter = () => {
        document.getElementById("volume-container").classList.add("show-volume");
        clearTimeout(this.volumeTimeout);
    }

    handleVolumeHoverLeaveTimeOut = () => {
        this.volumeTimeout = setTimeout(() => {
            document.getElementById("volume-container").classList.remove("show-volume");
        }, 1000);
    }

    // handleMarquee is call in changeImageAndTitleAuthor
    handleMarquee = idElement => {
        const element = document.getElementById(idElement);
        if (element.offsetWidth < element.scrollWidth) {
            element.classList.add("marquee");
        } else {
            element.classList.remove("marquee");
        }
    }

    onLoadImage = () => {
        let imageHTML = document.getElementById('image');
        let spectrumHTML = document.getElementById('spectrum');
        if (!activeSpectrum(this) || !spectrumHTML) {
            imageHTML.classList.remove("fadeOut");
        } else {
            spectrumHTML.classList.remove("fadeOut");
        }
    }

    ref = player => {
        this.player = player;
    }
}
